import React, { useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Tab-uri accesibile (role=tablist, săgeți stânga/dreapta), sincronizate opțional cu ?tab= din URL.
 * items: [{ id, label, count?, hidden? }]
 */
export function Tabs({ items, value, onChange, ariaLabel = "Secțiuni", className = "" }) {
  const refs = useRef([]);
  const visible = items.filter((t) => !t.hidden);
  const onKey = useCallback((e, idx) => {
    const dir = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!dir) return;
    e.preventDefault();
    const next = (idx + dir + visible.length) % visible.length;
    onChange(visible[next].id);
    refs.current[next]?.focus();
  }, [visible, onChange]);

  return (
    <div role="tablist" aria-label={ariaLabel} className={`cp-tabs ${className}`}>
      {visible.map((t, i) => (
        <button key={t.id} ref={(el) => (refs.current[i] = el)} role="tab" type="button"
          aria-selected={value === t.id} aria-controls={`panel-${t.id}`} id={`tab-${t.id}`}
          tabIndex={value === t.id ? 0 : -1} onKeyDown={(e) => onKey(e, i)}
          onClick={() => onChange(t.id)} className="cp-tab cp-focus-inset" data-testid={`tab-${t.id}`}>
          {t.label}
          {t.count != null && <span className={`cp-tab-count ${t.countTone === "neutral" ? "is-neutral" : ""}`}>{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function TabPanel({ id, active, children, className = "" }) {
  if (!active) return null;
  return <div role="tabpanel" id={`panel-${id}`} aria-labelledby={`tab-${id}`} className={`cp-rise ${className}`}>{children}</div>;
}

/** Hook: tab curent din ?tab=, cu fallback */
export function useTabParam(defaultTab, allowed) {
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  const value = raw && (!allowed || allowed.includes(raw)) ? raw : defaultTab;
  const set = useCallback((id) => {
    const next = new URLSearchParams(params);
    if (id === defaultTab) next.delete("tab"); else next.set("tab", id);
    setParams(next, { replace: true });
  }, [params, setParams, defaultTab]);
  return [value, set];
}

/** Filtru segmentat (aria-pressed) */
export function Segmented({ options, value, onChange, ariaLabel = "Filtru", className = "" }) {
  return (
    <div role="group" aria-label={ariaLabel} className={`cp-seg ${className}`}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}
          className="cp-seg-item" data-testid={`filter-${o.value}`}>
          {o.label}{o.count != null && <span className="cp-seg-count">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}
